/**
 * Why a capture window could not open the microphone, when the singer can do something about it.
 *
 * - `blocked`: the user or the browser refused permission (`NotAllowedError`), or the page is not
 *   one a browser will open a microphone on (`SecurityError`). Fixed in the browser's site settings.
 * - `missing`: there is no input device (`NotFoundError`), or the one the capture asked for has
 *   gone (`OverconstrainedError` on a remembered `deviceId`). Fixed by plugging one in.
 * - `in-use`: permission was granted, but the system would not hand over the device
 *   (`NotReadableError`), usually because another app holds it. Fixed by closing that app.
 */
export type MicAccessFailure = 'blocked' | 'missing' | 'in-use'

const failureByName: Record<string, MicAccessFailure> = {
  NotAllowedError: 'blocked',
  SecurityError: 'blocked',
  NotFoundError: 'missing',
  OverconstrainedError: 'missing',
  NotReadableError: 'in-use',
}

/**
 * Classify a rejection from `PitchCapture.start` (GH #2327). Null for anything else. A failure
 * that is not about access, such as a worklet that will not load, is a bug and must not be shown
 * to the singer as "allow your microphone".
 */
export const micAccessFailure = (error: unknown): MicAccessFailure | null =>
  error instanceof DOMException ? (failureByName[error.name] ?? null) : null
